// Google Places JS loader — the site's Places API key has HTTP referer
// restrictions, so Places calls MUST originate from the browser. This helper
// injects the Maps JS script once, then uses PlacesService.getDetails() to
// pull the boutique's live rating + review snippets and normalizes the shape
// to match what the backend cache expects.

const PLACES_FIELDS = ["rating", "user_ratings_total", "reviews", "photos", "name"];
const MAX_PLACE_PHOTOS = 8;
const PHOTO_MAX_WIDTH = 900;

let scriptPromise = null;

function loadPlacesScript(apiKey) {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("gplaces-js");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google), { once: true });
      existing.addEventListener("error", () => reject(new Error("Places JS failed")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.id = "gplaces-js";
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error("Places JS failed to load"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function normalizeReview(r) {
  // Legacy Places Details rarely populates per-review photos, but the newer
  // Places API v1 does. Pass through anything the SDK gives us so cards can
  // render "real proof" under the comment when it's available.
  const rawPhotos = Array.isArray(r.photos) ? r.photos : [];
  const photos = rawPhotos
    .map((p) => {
      if (typeof p === "string") return p;
      if (typeof p?.getUrl === "function") {
        try { return p.getUrl({ maxWidth: PHOTO_MAX_WIDTH }); } catch (_e) { return null; }
      }
      return p?.url || p?.uri || null;
    })
    .filter(Boolean);
  return {
    author_name: r.author_name || null,
    profile_photo_url: r.profile_photo_url || null,
    rating: r.rating ?? null,
    relative_time_description: r.relative_time_description || null,
    text: r.text || null,
    time: r.time ?? null,
    photos,
  };
}

/**
 * Fetch fresh Google Places review data for the given placeId using the
 * browser-side Places JS SDK. Returns `{ rating, total, reviews, photos }` —
 * where `photos` is a list of URLs from `place.photos[]` (customer + owner
 * uploads of the business). Never throws (resolves with nulls on failure so
 * callers can safely fall back to cached data).
 */
export async function fetchGooglePlacesReviews({ apiKey, placeId }) {
  if (!apiKey || !placeId) return null;
  try {
    const google = await loadPlacesScript(apiKey);
    return await new Promise((resolve) => {
      // PlacesService needs a DOM node — an offscreen div is fine.
      const host = document.createElement("div");
      const svc = new google.maps.places.PlacesService(host);
      svc.getDetails(
        { placeId, fields: PLACES_FIELDS },
        (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
            resolve(null);
            return;
          }
          const photos = (place.photos || [])
            .slice(0, MAX_PLACE_PHOTOS)
            .map((p) => {
              try { return p.getUrl({ maxWidth: PHOTO_MAX_WIDTH }); } catch (_e) { return null; }
            })
            .filter(Boolean);
          resolve({
            rating: typeof place.rating === "number" ? place.rating : null,
            total: place.user_ratings_total || 0,
            reviews: (place.reviews || []).map(normalizeReview),
            photos,
          });
        }
      );
    });
  } catch (_e) {
    return null;
  }
}
